/**
 * Single source of truth for job workflow status — keys, order, labels,
 * phase grouping and color tone. Every component that needs a status
 * label/color/position should import from here instead of keeping its
 * own copy.
 *
 * Labels and descriptions are resolved via i18next so they track the
 * active language automatically.
 */
import i18next from 'i18next';

export const JOB_STATUS = {
    NEW: 'NEW',
    CONFIRMED: 'CONFIRMED',
    SCHEDULED: 'SCHEDULED',
    ARRIVED: 'ARRIVED',
    IN_PROGRESS: 'IN_PROGRESS',
    FINISHING: 'FINISHING',
    READY: 'READY',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};

export const JOB_STATUS_ORDER = [
    JOB_STATUS.NEW,
    JOB_STATUS.CONFIRMED,
    JOB_STATUS.SCHEDULED,
    JOB_STATUS.ARRIVED,
    JOB_STATUS.IN_PROGRESS,
    JOB_STATUS.FINISHING,
    JOB_STATUS.READY,
    JOB_STATUS.COMPLETED,
];

const t = (key, fallback) => {
    const result = i18next.t(key);
    return result !== key ? result : (fallback || key);
};

export const PHASE_LABEL = new Proxy({}, {
    get: (_, phase) => t(`phaseLabel.${phase}`, String(phase)),
});

const TONES = {
    [JOB_STATUS.NEW]: 'grey',
    [JOB_STATUS.CONFIRMED]: 'grey',
    [JOB_STATUS.SCHEDULED]: 'grey',
    [JOB_STATUS.ARRIVED]: 'amber',
    [JOB_STATUS.IN_PROGRESS]: 'amber',
    [JOB_STATUS.FINISHING]: 'amber',
    [JOB_STATUS.READY]: 'green',
    [JOB_STATUS.COMPLETED]: 'green',
    [JOB_STATUS.CANCELLED]: 'cancelled',
};

const PHASES = {
    [JOB_STATUS.NEW]: 'booking',
    [JOB_STATUS.CONFIRMED]: 'booking',
    [JOB_STATUS.SCHEDULED]: 'booking',
    [JOB_STATUS.ARRIVED]: 'active',
    [JOB_STATUS.IN_PROGRESS]: 'active',
    [JOB_STATUS.FINISHING]: 'active',
    [JOB_STATUS.READY]: 'done',
    [JOB_STATUS.COMPLETED]: 'done',
    [JOB_STATUS.CANCELLED]: 'cancelled',
};

export const JOB_STATUS_META = new Proxy({}, {
    get: (_, status) => ({
        label: t(`jobStatus.${status}`, status),
        desc: t(`jobStatusDesc.${status}`, ''),
        tone: TONES[status] || 'grey',
        phase: PHASES[status] || 'booking',
    }),
});

export const LEGACY_STATUS_MAP = {
    ASSIGNED: JOB_STATUS.SCHEDULED,
    PICKUP_IN_PROGRESS: JOB_STATUS.ARRIVED,
    PICKUP_COMPLETED: JOB_STATUS.IN_PROGRESS,
    CLEANING: JOB_STATUS.IN_PROGRESS,
    FINALIZING: JOB_STATUS.FINISHING,
    CLEANING_COMPLETED: JOB_STATUS.FINISHING,
    DISPATCH_IN_PROGRESS: JOB_STATUS.READY,
};

export const STATUS_GROUPS = Object.values(JOB_STATUS).reduce((groups, canonical) => {
    groups[canonical] = [canonical];
    return groups;
}, {});
Object.entries(LEGACY_STATUS_MAP).forEach(([legacy, canonical]) => {
    STATUS_GROUPS[canonical].push(legacy);
});

export const normalizeStatus = (status) =>
    LEGACY_STATUS_MAP[status] || (JOB_STATUS[status] ? status : JOB_STATUS.SCHEDULED);

export const getStatusMeta = (status) => JOB_STATUS_META[normalizeStatus(status)];
export const getStatusLabel = (status) => getStatusMeta(status).label;
export const getStatusPhase = (status) => getStatusMeta(status).phase;
export const getStatusTone = (status) => getStatusMeta(status).tone;
export const getStatusIndex = (status) => JOB_STATUS_ORDER.indexOf(normalizeStatus(status));
export const isCancelled = (status) => status === JOB_STATUS.CANCELLED;

/**
 * Status filter / picker options in the active language. Call it while
 * rendering — a module-level copy would freeze the labels in whatever
 * language was active when the file was first imported.
 */
export const getStatusOptions = () =>
    [...JOB_STATUS_ORDER, JOB_STATUS.CANCELLED].map((status) => ({ value: status, label: getStatusLabel(status) }));

export const getSkippedStages = (fromStatus, toStatus) => {
    const fromIdx = getStatusIndex(fromStatus);
    const toIdx = getStatusIndex(toStatus);
    if (fromIdx === -1 || toIdx === -1 || toIdx <= fromIdx + 1) return [];
    return JOB_STATUS_ORDER.slice(fromIdx + 1, toIdx).map(getStatusLabel);
};
